import { META_PIXEL_ID } from './config.ts';

// The Meta Pixel global is a callable stub that also carries queue/version
// metadata; fbevents.js drains `queue` once it loads.
interface FbqStub {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  push: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    fbq?: FbqStub;
    _fbq?: FbqStub;
  }
}

/**
 * Idempotently bootstrap the Meta Pixel (connect.facebook.net/en_US/fbevents.js)
 * and fire the initial-load PageView here. Subsequent SPA navigations are
 * reported via {@link trackPageView} — one PageView per surface, no double count.
 */
export function loadPixel(): void {
  if (window.fbq) return;

  const fbq = function (this: unknown, ...args: unknown[]): void {
    // Before fbevents.js loads, buffer calls; after, route them through it.
    // Reflect.apply keeps `this === fbq` like Meta's official snippet.
    if (fbq.callMethod) {
      Reflect.apply(fbq.callMethod, fbq, args);
    } else {
      fbq.queue.push(args);
    }
  } as FbqStub;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);

  fbq('init', META_PIXEL_ID);
  fbq('track', 'PageView');
}

/** Fire one Meta Pixel PageView (used for SPA route changes after load). */
export function trackPageView(): void {
  window.fbq?.('track', 'PageView');
}
